import { AgendaAction, AgendaItem } from "../../../entities/agenda/Agenda";

export const AGENDA_PAGE_SIZE = 10;
export const AGENDA_SUMMARY_LIMIT = 5;

export type AgendaPriorityLevel = "high" | "medium" | "low";
export type AgendaTab = "all" | "pending" | "completed";

export const STATUS_PENDING = "pendiente";
export const STATUS_COMPLETED = "completado";

/** prioridad 1-2 → high, >2-3.5 → medium, >3.5 → low, null → no badge. */
export function getPriorityLevel(
  priority: number | null | undefined
): AgendaPriorityLevel | null {
  if (priority === null || priority === undefined || Number.isNaN(priority)) {
    return null;
  }
  if (priority <= 2) {
    return "high";
  }
  if (priority <= 3.5) {
    return "medium";
  }
  return "low";
}

export const isCompleted = (item: AgendaItem) =>
  item.status === STATUS_COMPLETED;

export function getTopPendingItems(
  items: AgendaItem[],
  limit: number = AGENDA_SUMMARY_LIMIT
): AgendaItem[] {
  return items.filter((x) => x.status === STATUS_PENDING).slice(0, limit);
}

export interface AgendaProgress {
  completed: number;
  total: number;
  percent: number;
}

export function getProgress(items: AgendaItem[]): AgendaProgress {
  const total = items.length;
  const completed = items.filter(isCompleted).length;
  return {
    completed,
    total,
    percent: total > 0 ? Math.round((completed / total) * 100) : 0
  };
}

/** Lowercase and strip diacritics so "Ferretería" matches "ferreteria". */
export function normalizeText(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function filterAgendaItems(
  items: AgendaItem[],
  tab: AgendaTab,
  search: string
): AgendaItem[] {
  const term = normalizeText(search);
  return items.filter((item) => {
    if (tab === "pending" && isCompleted(item)) {
      return false;
    }
    if (tab === "completed" && !isCompleted(item)) {
      return false;
    }
    return term === "" || normalizeText(item.nombre).includes(term);
  });
}

export function countByTab(items: AgendaItem[]): Record<AgendaTab, number> {
  const completed = items.filter(isCompleted).length;
  return {
    all: items.length,
    pending: items.length - completed,
    completed
  };
}

export interface AgendaPage<T> {
  pageItems: T[];
  pageCount: number;
  /** Requested page clamped to the valid range (zero based). */
  page: number;
}

export function paginate<T>(
  items: T[],
  page: number,
  pageSize: number = AGENDA_PAGE_SIZE
): AgendaPage<T> {
  const pageCount = Math.ceil(items.length / pageSize);
  const safePage = Math.min(Math.max(page, 0), Math.max(pageCount - 1, 0));
  const start = safePage * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    pageCount,
    page: safePage
  };
}

export type AgendaActionKind =
  | "email"
  | "call"
  | "whatsapp"
  | "meeting"
  | "task"
  | "escalate"
  | "unknown";

export interface AgendaActionMeta {
  kind: AgendaActionKind;
  /** Font Awesome classes. */
  icon: string;
  /** Translation key for the label. */
  labelKey: string;
  /** Translation key for the primary drawer button; undefined for unknown. */
  primaryKey?: string;
  /** Translation key for the drawer draft textarea label. */
  draftKey: string;
}

const ACTION_META: Record<AgendaAction, AgendaActionMeta> = {
  [AgendaAction.Email]: {
    kind: "email",
    icon: "fa-solid fa-envelope",
    labelKey: "AgendaActionEmail",
    primaryKey: "AgendaPrimaryEmail",
    draftKey: "AgendaDraftEmail"
  },
  [AgendaAction.Llamada]: {
    kind: "call",
    icon: "fa-solid fa-phone",
    labelKey: "AgendaActionCall",
    primaryKey: "AgendaPrimaryCall",
    draftKey: "AgendaDraftTalkingPoints"
  },
  [AgendaAction.Whatsapp]: {
    kind: "whatsapp",
    icon: "fa-brands fa-whatsapp",
    labelKey: "AgendaActionWhatsapp",
    primaryKey: "AgendaPrimaryWhatsapp",
    draftKey: "AgendaDraftMessage"
  },
  [AgendaAction.Reunion]: {
    kind: "meeting",
    icon: "fa-solid fa-users",
    labelKey: "AgendaActionMeeting",
    primaryKey: "AgendaPrimaryMeeting",
    draftKey: "AgendaDraftTalkingPoints"
  },
  [AgendaAction.Tarea]: {
    kind: "task",
    icon: "fa-solid fa-list-check",
    labelKey: "AgendaActionTask",
    primaryKey: "AgendaPrimaryTask",
    draftKey: "AgendaDraftTask"
  },
  [AgendaAction.Escalar]: {
    kind: "escalate",
    icon: "fa-solid fa-arrow-up-right-dots",
    labelKey: "AgendaActionEscalate",
    primaryKey: "AgendaPrimaryEscalate",
    draftKey: "AgendaDraftSummary"
  }
};

const UNKNOWN_ACTION_META: AgendaActionMeta = {
  kind: "unknown",
  icon: "fa-solid fa-bolt",
  labelKey: "AgendaActionUnknown",
  draftKey: "AgendaDraftGeneric"
};

export function getActionMeta(action: number): AgendaActionMeta {
  return ACTION_META[action as AgendaAction] ?? UNKNOWN_ACTION_META;
}

/** Calendar days between two dates (ignores the time of day). */
export function getDaysSince(date: Date, now: Date = new Date()): number {
  const start = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const end = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(Math.round((end - start) / 86_400_000), 0);
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Converts LLM plain text into safe HTML for the email editor: every character
 * is escaped, blank lines split paragraphs and single line breaks become <br>.
 */
export function plainTextToHtml(text: string | null | undefined): string {
  const value = (text ?? "").trim();
  if (value === "") {
    return "";
  }
  return value
    .split(/\r?\n\s*\r?\n/)
    .map(
      (paragraph) => `<p>${escapeHtml(paragraph).replace(/\r?\n/g, "<br>")}</p>`
    )
    .join("");
}

/** True when the failed request is the backend's 402 insufficient_balance. */
export function isInsufficientBalanceError(result: unknown): boolean {
  if (!(result instanceof Error)) {
    return false;
  }
  const response = (
    result as { response?: { status?: number; data?: { error?: string } } }
  ).response;
  return (
    response?.status === 402 || response?.data?.error === "insufficient_balance"
  );
}
